import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from 'styled-components';
import { getAvatarGradient, getInitials } from 'utils';
import useAnchored, { AnchorPoint } from 'hooks/useAnchored';
import { MentionUser } from '../mentions';
import * as S from './styles';

interface MentionPickerProps {
	mentionUsers: MentionUser[];
	selectedIndex: number;
	anchor: AnchorPoint | null;
	onSelect: (mentionUser: MentionUser) => void;
	onHoverIndex: (index: number) => void;
}

// Rendered through a portal so it can hang wider than the comments panel —
// the panel is user-resizable down to 100px and clips overflow, so the menu
// ignores the panel edge entirely and clamps only to the viewport. Email is
// always shown: it's the only disambiguator between identical display names.
const MentionPicker = ({
	mentionUsers,
	selectedIndex,
	anchor,
	onSelect,
	onHoverIndex,
}: MentionPickerProps) => {
	const theme = useTheme();
	const { ref, style } = useAnchored(anchor, mentionUsers.length);

	//----------------------------------------------------------------------
	// HOOKS
	//----------------------------------------------------------------------

	useEffect(() => {
		ref.current
			?.querySelector(`[data-row="${selectedIndex}"]`)
			?.scrollIntoView({ block: 'nearest' });
	}, [selectedIndex]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	if (mentionUsers.length === 0) return null;

	return createPortal(
		<S.Base ref={ref} style={style}>
			{mentionUsers.map((mentionUser, i) => (
				<S.Row
					key={mentionUser.id}
					data-row={i}
					$selected={i === selectedIndex}
					onMouseMove={() => onHoverIndex(i)}
					// mousedown, not click: the textarea must keep focus through the pick
					onMouseDown={(e) => {
						e.preventDefault();
						onSelect(mentionUser);
					}}
				>
					<S.Avatar style={{ background: getAvatarGradient(mentionUser.displayName, theme) }}>
						{getInitials(mentionUser.displayName)}
					</S.Avatar>
					<S.Name $selected={i === selectedIndex}>
						{mentionUser.displayName}
						{mentionUser.isSelf ? ' (you)' : ''}
					</S.Name>
					<S.Email $selected={i === selectedIndex}>{mentionUser.email}</S.Email>
				</S.Row>
			))}
		</S.Base>,
		document.body
	);
};

export default MentionPicker;
